import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Lock,
  Search,
  UserPlus,
  Send,
  PlusCircle,
  Bold,
  Smile,
  AtSign,
  Bot,
  User,
  FileText,
  Workflow,
  ChevronDown,
  Sparkles,
  Zap,
  Globe,
  Radio,
  Hash,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';

interface MockMessage {
  id: string;
  sender: string;
  text: string;
  isBot: boolean;
  time: string;
  avatarBg?: string;
  reactions?: Array<{ emoji: string; count: number; active?: boolean }>;
}

const INITIAL_MESSAGES: Record<string, MockMessage[]> = {
  general: [
    {
      id: 'g1',
      sender: 'SahajBot',
      text: "Hello! I'm SahajBot. I can help summarize threads, find documents, or trigger automated workflows. Try typing a message below to test live collaboration!",
      isBot: true,
      time: '12:37 PM',
      avatarBg: 'bg-gradient-to-tr from-[#292a73] to-[#3a9efd]',
      reactions: [{ emoji: '⚡', count: 6, active: true }, { emoji: '👋', count: 12 }],
    },
    {
      id: 'g2',
      sender: 'Staff Architect',
      text: 'Real-time WebSocket STOMP synchronization and cross-platform channels are live with 0ms duplication! 🚀',
      isBot: false,
      time: '12:39 PM',
      avatarBg: 'bg-gradient-to-tr from-[#1a1b4b] to-[#3e4491]',
      reactions: [{ emoji: '🔥', count: 9 }],
    },
  ],
  'design-system': [
    {
      id: 'd1',
      sender: 'SahajBot',
      text: 'Welcome to #design-system. The brand palette (#1a1b4b, #292a73, #3a9efd, #3e4491, #f7a400) has been synchronized across all components.',
      isBot: true,
      time: '11:15 AM',
      avatarBg: 'bg-gradient-to-tr from-[#3e4491] to-[#3a9efd]',
      reactions: [{ emoji: '🎨', count: 8, active: true }],
    },
  ],
  engineering: [
    {
      id: 'e1',
      sender: 'SahajBot',
      text: 'Release v1.4 deployment is successful! Real-time message broker and multi-tenant security architecture are verified.',
      isBot: true,
      time: '10:04 AM',
      avatarBg: 'bg-gradient-to-tr from-[#1a1b4b] to-[#f7a400]',
      reactions: [{ emoji: '🚀', count: 14, active: true }, { emoji: '🎉', count: 7 }],
    },
  ],
};

const TRUSTED_COMPANIES = [
  'Acme Global',
  'Vertex Systems',
  'Nexus Cloud',
  'HyperScale',
  'Aura Labs',
  'Vanguard AI',
];

export const HomePage: React.FC = () => {
  const { isAuthenticated } = useAuth();

  // Chat Simulator State
  const [currentChannel, setCurrentChannel] = useState<'general' | 'design-system' | 'engineering'>('general');
  const [messages, setMessages] = useState<Record<string, MockMessage[]>>(INITIAL_MESSAGES);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatMessagesEndRef = useRef<HTMLDivElement>(null);

  const activeMessages = messages[currentChannel] || [];

  const scrollToBottom = () => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [activeMessages, isTyping]);

  const handleSendMessage = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const text = inputText.trim();
    if (!text) return;

    const timeString = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const userMsg: MockMessage = {
      id: `user-${Date.now()}`,
      sender: 'You',
      text,
      isBot: false,
      time: timeString,
      avatarBg: 'bg-gradient-to-tr from-[#1a1b4b] to-[#292a73]',
    };

    setMessages((prev) => ({
      ...prev,
      [currentChannel]: [...(prev[currentChannel] || []), userMsg],
    }));
    setInputText('');

    // Trigger typing indicator and bot reply
    setTimeout(() => {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        const botMsg: MockMessage = {
          id: `bot-${Date.now()}`,
          sender: 'SahajBot',
          text: `Got your update in #${currentChannel}! ✨ Sahaj Connect is synchronizing your messages across Web, Desktop, and Mobile in real time.`,
          isBot: true,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          avatarBg: 'bg-gradient-to-tr from-[#292a73] to-[#3a9efd]',
          reactions: [{ emoji: '✨', count: 1, active: true }],
        };
        setMessages((prev) => ({
          ...prev,
          [currentChannel]: [...(prev[currentChannel] || []), botMsg],
        }));
      }, 1100);
    }, 350);
  };

  const handleToggleReaction = (msgId: string, emoji: string) => {
    setMessages((prev) => {
      const channelMsgs = prev[currentChannel] || [];
      const updated = channelMsgs.map((m) => {
        if (m.id !== msgId) return m;
        const reactions = m.reactions || [];
        const existing = reactions.find((r) => r.emoji === emoji);
        let nextReactions;
        if (existing) {
          if (existing.active) {
            nextReactions = reactions.map((r) =>
              r.emoji === emoji
                ? { ...r, count: Math.max(0, r.count - 1), active: false }
                : r
            );
          } else {
            nextReactions = reactions.map((r) =>
              r.emoji === emoji
                ? { ...r, count: r.count + 1, active: true }
                : r
            );
          }
        } else {
          nextReactions = [...reactions, { emoji, count: 1, active: true }];
        }
        return { ...m, reactions: nextReactions.filter((r) => r.count > 0) };
      });
      return { ...prev, [currentChannel]: updated };
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F9FB] text-[#1a1b4b] font-sans antialiased selection:bg-[#3a9efd]/20 selection:text-[#1a1b4b] overflow-x-hidden">
      
      {/* Top Navbar */}
      <Navbar />

      {/* ============================================================
          HERO SECTION (Palette: #1a1b4b, #292a73, #3a9efd, #3e4491, #f7a400)
          ============================================================ */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 px-4 sm:px-6 lg:px-8 text-center overflow-hidden">
        
        {/* Ambient Glow Spheres using strict palette */}
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[900px] h-[550px] bg-gradient-to-tr from-[#292a73]/15 via-[#3a9efd]/15 to-[#f7a400]/10 rounded-full blur-[110px] pointer-events-none -z-10" />
        <div className="absolute top-40 -left-20 w-[400px] h-[400px] bg-gradient-to-br from-[#3a9efd]/15 to-[#292a73]/10 rounded-full blur-[90px] pointer-events-none -z-10" />
        <div className="absolute top-60 -right-20 w-[450px] h-[450px] bg-gradient-to-bl from-[#f7a400]/15 via-[#3e4491]/10 to-transparent rounded-full blur-[100px] pointer-events-none -z-10" />

        <div className="max-w-4xl mx-auto relative z-10">

          {/* Main Hero Headline */}
          <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-[64px] leading-[1.12] mb-5 font-black tracking-tight text-[#1a1b4b]">
            Connect easily. Communicate clearly.{' '}
            <span className="block sm:inline text-[#3e4491]">
              Work better together.
            </span>
          </h1>

          {/* Hero Subtitle */}
          <p className="text-sm sm:text-lg md:text-xl text-[#3e4491] max-w-2xl mx-auto mb-8 font-medium leading-relaxed">
            Connect with your team, communicate effortlessly, and keep everyone in sync — all from one simple workspace.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-3.5">
            {isAuthenticated ? (
              <Link
                to="/app"
                className="w-full sm:w-auto bg-[#1a1b4b] hover:bg-[#292a73] text-white px-7 py-3.5 rounded-full text-base font-bold shadow-lg shadow-[#1a1b4b]/25 hover:shadow-xl hover:-translate-y-0.5 transition-all inline-flex items-center justify-center gap-2"
              >
                <span>Open Your Workspace</span>
                <ArrowRight className="h-4 w-4 text-[#f7a400]" />
              </Link>
            ) : (
              <Link
                to="/register"
                className="w-full sm:w-auto bg-[#1a1b4b] hover:bg-[#292a73] text-white px-7 py-3.5 rounded-full text-base font-bold shadow-lg shadow-[#1a1b4b]/25 hover:shadow-xl hover:-translate-y-0.5 transition-all inline-flex items-center justify-center gap-2"
              >
                <span>Get Started for Free</span>
                <ArrowRight className="h-4 w-4 text-[#f7a400]" />
              </Link>
            )}

            <Link
              to="/pricing"
              className="w-full sm:w-auto bg-white/90 text-[#1a1b4b] border-2 border-[#3e4491]/30 hover:border-[#3a9efd] px-7 py-3.5 rounded-full text-base font-bold hover:bg-[#F8FAFC] transition-all shadow-xs"
            >
              Find your subscription
            </Link>
          </div>
        </div>

        {/* ============================================================
            MAIN VISUAL WORKSPACE MOCKUP (Interactive Simulator)
            ============================================================ */}
        <div className="max-w-7xl mx-auto mt-16 relative z-20 px-2 sm:px-4 text-left">
          
          {/* Subtle Ambient Glow Behind Mockup */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#292a73]/20 via-[#3a9efd]/15 to-[#f7a400]/15 rounded-[2.5rem] blur-2xl -z-10 transform scale-95" />

          <div className="relative rounded-[2rem] border-2 border-[#3e4491]/20 bg-white/95 backdrop-blur-2xl shadow-2xl shadow-[#1a1b4b]/10 overflow-hidden flex flex-col h-[650px] ring-1 ring-[#292a73]/10">
            
            {/* macOS Chrome Bar */}
            <div className="h-12 bg-gradient-to-r from-[#F8FAFC] via-[#F1F5F9] to-[#F8FAFC] border-b border-[#3e4491]/20 flex items-center px-4 gap-3 shrink-0">
              <div className="flex gap-2">
                <div className="w-3.5 h-3.5 rounded-full bg-[#f7a400] shadow-xs" />
                <div className="w-3.5 h-3.5 rounded-full bg-[#3a9efd] shadow-xs" />
                <div className="w-3.5 h-3.5 rounded-full bg-[#292a73] shadow-xs" />
              </div>
              <div className="mx-auto flex-1 max-w-md bg-white border border-[#3e4491]/25 rounded-lg h-7 flex items-center justify-center text-xs text-[#3e4491] font-mono shadow-2xs">
                <Lock className="h-3.5 w-3.5 mr-1.5 text-[#3a9efd]" />
                <span>app.sahajconnect.com</span>
                <span className="ml-2 text-[10px] bg-[#3a9efd]/15 text-[#292a73] px-1.5 py-0.2 rounded font-sans font-bold">LIVE</span>
              </div>
            </div>

            {/* Interactive Workspace Body */}
            <div className="flex-1 flex overflow-hidden bg-white text-[#1a1b4b]">
              
              {/* Workspace Rail (Leftmost) */}
              <div className="w-16 bg-[#1a1b4b] flex flex-col items-center py-4 gap-3.5 shrink-0 hidden sm:flex border-r border-[#292a73]">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-[#292a73] to-[#3a9efd] text-white flex items-center justify-center font-black text-sm shadow-md shadow-[#3a9efd]/30 ring-2 ring-white/30 cursor-pointer">
                  S
                </div>
                <div className="w-10 h-10 rounded-xl bg-white/10 hover:bg-[#292a73] text-[#3a9efd] flex items-center justify-center font-bold text-xs transition cursor-pointer">
                  <Zap className="h-5 w-5" />
                </div>
                <div className="w-10 h-10 rounded-xl bg-white/10 hover:bg-[#292a73] text-[#f7a400] flex items-center justify-center font-bold text-xs transition cursor-pointer">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div className="w-10 h-10 rounded-xl bg-white/5 text-slate-400 flex items-center justify-center text-xs mt-auto cursor-pointer">
                  <Radio className="h-4 w-4 text-[#3a9efd]" />
                </div>
              </div>

              {/* Channel Sidebar */}
              <div className="w-64 sm:w-72 bg-[#F8FAFC] border-r border-[#3e4491]/20 flex flex-col shrink-0 hidden md:flex shadow-xs z-20">
                <div className="p-4 border-b border-[#3e4491]/20 flex items-center gap-2.5 font-bold text-[#1a1b4b] text-base hover:bg-slate-100 transition-colors cursor-pointer">
                  <div className="bg-gradient-to-tr from-[#1a1b4b] to-[#292a73] p-1 rounded-lg text-white font-black text-xs flex items-center justify-center w-6 h-6 shadow-xs">
                    HQ
                  </div>
                  <span>Sahaj Headquarters</span>
                  <ChevronDown className="ml-auto text-[#3e4491] h-4 w-4" />
                </div>

                <div className="p-3.5 flex-1 overflow-y-auto space-y-6 text-sm">
                  {/* Channels */}
                  <div>
                    <h3 className="text-[11px] font-bold text-[#3e4491] uppercase tracking-wider mb-2.5 px-2 flex items-center justify-between">
                      <span>Channels</span>
                      <PlusCircle className="h-3.5 w-3.5 text-[#3e4491] hover:text-[#3a9efd] cursor-pointer" />
                    </h3>
                    <ul className="space-y-1">
                      {[
                        { id: 'general', name: 'general', tag: 'All Team' },
                        { id: 'design-system', name: 'design-system', tag: 'Tokens' },
                        { id: 'engineering', name: 'engineering', tag: 'v1.4 Live' },
                      ].map((ch) => {
                        const isSelected = currentChannel === ch.id;
                        return (
                          <li key={ch.id}>
                            <button
                              type="button"
                              onClick={() => setCurrentChannel(ch.id as any)}
                              className={`w-full text-left px-3 py-2 rounded-xl text-xs sm:text-sm font-bold flex items-center justify-between transition-all cursor-pointer ${
                                isSelected
                                  ? 'bg-[#292a73] text-white shadow-sm ring-1 ring-[#3a9efd]'
                                  : 'text-[#3e4491] hover:bg-slate-200/60 hover:text-[#1a1b4b]'
                              }`}
                            >
                              <span className="flex items-center gap-2">
                                <span className={isSelected ? 'text-[#3a9efd] font-black' : 'text-[#3e4491]'}>#</span>
                                <span>{ch.name}</span>
                              </span>
                              <span className={`text-[10px] font-bold px-1.5 py-0.2 rounded ${isSelected ? 'bg-[#3a9efd] text-[#1a1b4b]' : 'bg-[#3e4491]/15 text-[#3e4491]'}`}>
                                {ch.tag}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  {/* Direct Messages */}
                  <div>
                    <h3 className="text-[11px] font-bold text-[#3e4491] uppercase tracking-wider mb-2.5 px-2 flex items-center justify-between">
                      <span>Direct Messages</span>
                      <PlusCircle className="h-3.5 w-3.5 text-[#3e4491] hover:text-[#3a9efd] cursor-pointer" />
                    </h3>
                    <ul className="space-y-1">
                      <li>
                        <div className="px-3 py-2 rounded-xl text-[#1a1b4b] hover:bg-slate-200/60 font-semibold text-xs sm:text-sm flex items-center gap-2.5 cursor-pointer transition-all">
                          <div className="relative">
                            <div className="w-6 h-6 rounded-lg bg-gradient-to-tr from-[#292a73] to-[#3a9efd] text-white flex items-center justify-center shadow-xs">
                              <Bot className="h-3.5 w-3.5" />
                            </div>
                            <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#f7a400] ring-2 ring-white" />
                          </div>
                          <span>SahajBot</span>
                          <span className="ml-auto text-[9px] bg-[#3a9efd]/15 text-[#292a73] font-extrabold px-1.5 py-0.2 rounded-full">AI</span>
                        </div>
                      </li>
                      <li>
                        <div className="px-3 py-2 rounded-xl text-[#1a1b4b] hover:bg-slate-200/60 font-semibold text-xs sm:text-sm flex items-center gap-2.5 cursor-pointer transition-all">
                          <div className="relative">
                            <div className="w-6 h-6 rounded-lg bg-gradient-to-tr from-[#1a1b4b] to-[#3e4491] text-white flex items-center justify-center text-[10px] font-bold shadow-xs">
                              SA
                            </div>
                            <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#3a9efd] ring-2 ring-white" />
                          </div>
                          <span>Staff Architect</span>
                        </div>
                      </li>
                      <li>
                        <div className="px-3 py-2 rounded-xl text-[#1a1b4b] hover:bg-slate-200/60 font-semibold text-xs sm:text-sm flex items-center gap-2.5 cursor-pointer transition-all">
                          <div className="relative">
                            <div className="w-6 h-6 rounded-lg bg-gradient-to-tr from-[#3e4491] to-[#f7a400] text-white flex items-center justify-center text-[10px] font-bold shadow-xs">
                              JD
                            </div>
                            <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#f7a400] ring-2 ring-white" />
                          </div>
                          <span>Jane Doe</span>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Chat Main View */}
              <div className="flex-1 flex flex-col bg-white relative min-w-0">
                {/* Header */}
                <div className="p-4 border-b border-[#3e4491]/20 flex items-center justify-between bg-white/80 backdrop-blur-md z-10 shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#3a9efd]/15 text-[#292a73] flex items-center justify-center font-bold">
                      <Hash className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[#1a1b4b] text-base tracking-tight">
                          {currentChannel}
                        </span>
                        <span className="bg-[#f7a400]/20 text-[#1a1b4b] text-[10px] font-bold px-2 py-0.2 rounded-full border border-[#f7a400]/40">
                          Active
                        </span>
                      </div>
                      <p className="text-[11px] text-[#3e4491] font-medium hidden sm:block">
                        High-velocity team collaboration & instant AI agent responses
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button className="p-2 text-[#3e4491] hover:text-[#1a1b4b] hover:bg-slate-100 rounded-xl transition-colors">
                      <Search className="h-4 w-4" />
                    </button>
                    <Link
                      to="/register"
                      className="bg-[#1a1b4b] hover:bg-[#292a73] text-white px-3.5 py-1.5 rounded-xl text-xs font-bold hover:shadow-md transition-all shadow-xs flex items-center gap-1.5"
                    >
                      <UserPlus className="h-3.5 w-3.5 text-[#f7a400]" />
                      <span>Invite Team</span>
                    </Link>
                  </div>
                </div>

                {/* Messages Feed */}
                <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-4 scroll-smooth bg-gradient-to-b from-white via-[#F8FAFC]/50 to-white">
                  <div className="text-center text-xs text-[#3e4491] mb-6 border-b border-[#3e4491]/15 pb-3">
                    <span>#{currentChannel} channel created. All team members can collaborate in real time.</span>
                  </div>

                  {activeMessages.map((msg) => (
                    <div key={msg.id} className="flex gap-3.5 animate-in fade-in slide-in-from-bottom-1 duration-200">
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-md text-white font-bold ${
                          msg.avatarBg || 'bg-gradient-to-tr from-[#1a1b4b] to-[#292a73]'
                        }`}
                      >
                        {msg.isBot ? <Bot className="h-4 w-4" /> : <User className="h-4 w-4" />}
                      </div>

                      <div className="flex-1 space-y-1 text-left">
                        <div className="flex items-baseline gap-2">
                          <span className="font-bold text-[#1a1b4b] text-sm">{msg.sender}</span>
                          {msg.isBot && (
                            <span className="bg-gradient-to-r from-[#292a73] to-[#3a9efd] text-white text-[9px] px-1.5 py-0.2 rounded font-extrabold tracking-wider">
                              AI BOT
                            </span>
                          )}
                          <span className="text-[10px] text-[#3e4491]">{msg.time}</span>
                        </div>

                        <div
                          className={`mt-1 leading-relaxed inline-block max-w-2xl text-xs sm:text-sm ${
                            msg.isBot
                              ? 'bg-gradient-to-br from-[#F8FAFC] to-[#F1F5F9] text-[#1a1b4b] p-3.5 rounded-2xl rounded-tl-sm border border-[#3e4491]/25 shadow-sm'
                              : 'bg-white p-3 rounded-2xl border border-[#3e4491]/20 text-[#1a1b4b] shadow-2xs'
                          }`}
                        >
                          {msg.text}
                        </div>

                        {/* Emoji Reactions */}
                        {msg.reactions && (
                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {msg.reactions.map((r, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => handleToggleReaction(msg.id, r.emoji)}
                                title={`${
                                  r.active
                                    ? r.count > 1
                                      ? `You and ${r.count - 1} other${r.count - 1 === 1 ? '' : 's'}`
                                      : 'You'
                                    : `${r.count} ${r.count === 1 ? 'person' : 'people'}`
                                } reacted with ${r.emoji} (Click to ${r.active ? 'remove' : 'add'} reaction)`}
                                className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold border flex items-center gap-1.5 transition-all duration-150 cursor-pointer shadow-2xs hover:scale-105 active:scale-95 select-none ${
                                  r.active
                                    ? 'bg-[#3a9efd]/20 border-[#3a9efd] text-[#1a1b4b] font-bold shadow-xs'
                                    : 'bg-white border-[#3e4491]/20 text-[#3e4491] hover:bg-slate-50 hover:border-[#3e4491]/40'
                                }`}
                              >
                                <span className="text-xs">{r.emoji}</span>
                                <span>{r.count}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Typing Indicator */}
                  {isTyping && (
                    <div className="px-2 py-1">
                      <div className="text-xs text-[#292a73] flex items-center gap-2 font-bold bg-[#3a9efd]/15 inline-flex px-3 py-1 rounded-full border border-[#3a9efd]/40 shadow-2xs">
                        <Bot className="h-3.5 w-3.5 text-[#292a73]" />
                        <span>SahajBot is synthesizing a response</span>
                        <span className="flex gap-1 ml-1">
                          <span className="w-1.5 h-1.5 bg-[#1a1b4b] rounded-full animate-bounce" />
                          <span className="w-1.5 h-1.5 bg-[#292a73] rounded-full animate-bounce [animation-delay:150ms]" />
                          <span className="w-1.5 h-1.5 bg-[#3a9efd] rounded-full animate-bounce [animation-delay:300ms]" />
                        </span>
                      </div>
                    </div>
                  )}

                  <div ref={chatMessagesEndRef} />
                </div>

                {/* Message Composer */}
                <div className="p-4 pt-2 shrink-0 bg-white border-t border-[#3e4491]/20">
                  <form
                    onSubmit={handleSendMessage}
                    className="bg-[#F8FAFC] rounded-2xl p-2.5 flex flex-col gap-2 focus-within:ring-2 focus-within:ring-[#3a9efd] focus-within:bg-white border border-[#3e4491]/30 shadow-sm transition group"
                  >
                    <input
                      type="text"
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      placeholder={`Message #${currentChannel}... (Type and test live real-time simulation)`}
                      className="w-full bg-transparent text-[#1a1b4b] border-none focus:outline-none placeholder:text-[#3e4491] text-xs sm:text-sm p-2"
                    />

                    <div className="flex justify-between items-center px-1 pt-1 border-t border-[#3e4491]/15">
                      <div className="flex gap-1 text-[#3e4491]">
                        <button type="button" className="hover:text-[#3a9efd] hover:bg-indigo-50 transition-colors p-1.5 rounded-lg">
                          <PlusCircle className="h-4 w-4" />
                        </button>
                        <button type="button" className="hover:text-[#3a9efd] hover:bg-indigo-50 transition-colors p-1.5 rounded-lg">
                          <Bold className="h-4 w-4" />
                        </button>
                        <button type="button" className="hover:text-[#3a9efd] hover:bg-indigo-50 transition-colors p-1.5 rounded-lg">
                          <Smile className="h-4 w-4" />
                        </button>
                        <div className="w-px h-4 bg-[#3e4491]/30 mx-1 self-center" />
                        <button type="button" className="hover:text-[#3a9efd] hover:bg-indigo-50 transition-colors p-1.5 rounded-lg font-bold">
                          <AtSign className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="flex items-center gap-2.5">
                        <span className="text-[10px] text-[#3e4491] font-medium hidden sm:inline-block">Return to send</span>
                        <button
                          type="submit"
                          disabled={!inputText.trim()}
                          className="bg-[#1a1b4b] hover:bg-[#292a73] text-white p-2 rounded-xl hover:shadow-md transition-all flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                        >
                          <Send className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          TRUST SECTION
          ============================================================ */}
      <section className="py-12 bg-white border-y border-[#3e4491]/20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-xs font-bold text-[#3e4491] uppercase tracking-widest mb-8">
            Trusted by innovative teams worldwide
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-80">
            {TRUSTED_COMPANIES.map((company, idx) => (
              <div key={idx} className="flex items-center gap-2 text-[#1a1b4b] font-bold text-sm tracking-wider uppercase">
                <span className="w-2 h-2 rounded-full bg-[#3a9efd]" />
                <span>{company}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          FEATURE DEEP-DIVES
          ============================================================ */}
      <section className="py-24 px-6 max-w-7xl mx-auto space-y-32">
        {/* Deep Dive 1: Intelligence at the core */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#292a73]/10 border border-[#3e4491]/25 text-[#292a73] text-xs font-bold mb-4">
              <Sparkles className="h-3.5 w-3.5 text-[#f7a400]" />
              <span>AI Intelligence</span>
            </div>
            <h2 className="text-4xl text-[#1a1b4b] font-bold mb-6 tracking-tight">
              Intelligence at the core.
            </h2>
            <p className="text-lg text-[#3e4491] mb-6 leading-relaxed">
              SahajBot isn't just an add-on; it's woven into the fabric of your workspaces. Get instant summaries, draft responses, and find information across all your channels without leaving the conversation.
            </p>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-[#3a9efd] h-5 w-5 mt-0.5 shrink-0" />
                <span className="text-[#1a1b4b] font-medium">Context-aware AI assistance</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-[#3a9efd] h-5 w-5 mt-0.5 shrink-0" />
                <span className="text-[#1a1b4b] font-medium">Automated thread summaries</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-[#3a9efd] h-5 w-5 mt-0.5 shrink-0" />
                <span className="text-[#1a1b4b] font-medium">Smart search across all apps</span>
              </li>
            </ul>
          </div>

          <div className="relative">
            <div className="rounded-2xl shadow-2xl border-2 border-[#3e4491]/20 bg-white p-6 space-y-4 max-w-md mx-auto">
              <div className="flex items-center gap-3 border-b border-[#3e4491]/20 pb-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-[#292a73] to-[#3a9efd] text-white flex items-center justify-center">
                  <Bot className="h-4 w-4" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-[#1a1b4b]">SahajBot Thread Assistant</h4>
                  <p className="text-xs text-[#3e4491]">Summarizing 14 replies in #design-system</p>
                </div>
              </div>
              <div className="bg-[#F8FAFC] p-4 rounded-xl text-xs space-y-2 text-[#1a1b4b] leading-relaxed border border-[#3e4491]/15">
                <p className="font-bold text-[#292a73]">Key Takeaways:</p>
                <p>• Palette tokens verified (#1a1b4b, #292a73, #3a9efd, #3e4491, #f7a400).</p>
                <p>• Full-size navigation bar and visible outline confirmed.</p>
                <p>• Release approved by product lead.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Deep Dive 2: Knowledge centralized */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <div className="order-2 md:order-1 relative">
            <div className="rounded-2xl shadow-2xl border-2 border-[#3e4491]/20 bg-white p-6 space-y-4 max-w-md mx-auto">
              <div className="flex items-center justify-between border-b border-[#3e4491]/20 pb-3">
                <span className="font-bold text-sm text-[#1a1b4b] flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[#3a9efd]" /> Central Knowledge Base
                </span>
                <span className="text-xs bg-[#3a9efd]/15 text-[#292a73] font-bold px-2 py-0.5 rounded">
                  Indexed
                </span>
              </div>
              <div className="space-y-2 text-xs">
                <div className="p-3 bg-[#F8FAFC] rounded-xl flex items-center justify-between border border-[#3e4491]/15">
                  <span className="font-medium text-[#1a1b4b]">Engineering Architecture Specs.pdf</span>
                  <span className="text-[#3e4491]">2.4 MB</span>
                </div>
                <div className="p-3 bg-[#F8FAFC] rounded-xl flex items-center justify-between border border-[#3e4491]/15">
                  <span className="font-medium text-[#1a1b4b]">Design Tokens & Variables.json</span>
                  <span className="text-[#3e4491]">48 KB</span>
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 md:order-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#3a9efd]/15 border border-[#3a9efd]/30 text-[#292a73] text-xs font-bold mb-4">
              <Globe className="h-3.5 w-3.5 text-[#3a9efd]" />
              <span>Unified Knowledge</span>
            </div>
            <h2 className="text-4xl text-[#1a1b4b] font-bold mb-6 tracking-tight">
              Knowledge centralized.
            </h2>
            <p className="text-lg text-[#3e4491] mb-6 leading-relaxed">
              Bring your entire team's knowledge into one searchable, organized space. Say goodbye to scattered documents and lost information.
            </p>
            <Link to="/features" className="text-[#292a73] hover:text-[#3a9efd] font-bold inline-flex items-center gap-1.5 transition">
              <span>Explore Knowledge Base</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================
          PRODUCTIVITY STATS BAR
          ============================================================ */}
      <section className="py-20 bg-gradient-to-r from-[#1a1b4b] via-[#292a73] to-[#3e4491] text-white text-center shadow-lg border-y border-[#292a73]">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-5xl font-black mb-2 text-[#3a9efd]">97</h3>
            <p className="text-[#F8FAFC] font-medium uppercase tracking-wider text-sm">
              Minutes saved weekly
            </p>
          </div>
          <div>
            <h3 className="text-5xl font-black mb-2 text-[#f7a400]">35%</h3>
            <p className="text-[#F8FAFC] font-medium uppercase tracking-wider text-sm">
              Increase in efficiency
            </p>
          </div>
          <div>
            <h3 className="text-5xl font-black mb-2 text-[#3a9efd]">10x</h3>
            <p className="text-[#F8FAFC] font-medium uppercase tracking-wider text-sm">
              Faster onboarding
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================
          PROCESS SECTION (Automated Workflow)
          ============================================================ */}
      <section className="py-32 px-6 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-24 items-center">
            {/* Visual Workflow Canvas */}
            <div className="order-2 md:order-1 relative">
              <div className="bg-white rounded-[2rem] p-8 border-2 border-[#3e4491]/20 shadow-lg">
                <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-[#3e4491]/20">
                  <div className="flex items-center justify-between border-b border-[#3e4491]/20 pb-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#292a73] flex items-center justify-center text-white shadow-xs">
                        <Workflow className="h-4 w-4" />
                      </div>
                      <span className="font-bold text-[#1a1b4b]">Expense Approval</span>
                    </div>
                    <span className="bg-[#f7a400]/20 text-[#1a1b4b] border border-[#f7a400]/40 px-2.5 py-1 rounded text-xs font-bold uppercase">
                      Active
                    </span>
                  </div>

                  <div className="space-y-4 relative pl-6 border-l-2 border-[#292a73]">
                    <div className="absolute w-3 h-3 bg-[#3a9efd] rounded-full -left-[7px] top-4 ring-4 ring-blue-50" />
                    <div className="bg-white p-4 rounded-xl border border-[#3e4491]/20 shadow-2xs">
                      <div className="text-xs text-[#292a73] font-bold uppercase mb-1">Trigger</div>
                      <div className="text-[#1a1b4b] font-medium text-sm">New item added to List: Expenses</div>
                    </div>

                    <div className="absolute w-3 h-3 bg-[#3a9efd] rounded-full -left-[7px] top-[100px] ring-4 ring-blue-50" />
                    <div className="bg-white p-4 rounded-xl border border-[#3e4491]/20 shadow-2xs mt-4">
                      <div className="text-xs text-[#292a73] font-bold uppercase mb-1">Action</div>
                      <div className="text-[#1a1b4b] font-medium text-sm">Send approval request to Manager</div>
                    </div>

                    <div className="bg-[#F8FAFC] p-4 rounded-xl border-2 border-dashed border-[#3e4491]/30 mt-4 text-center cursor-pointer hover:bg-slate-100 transition-colors">
                      <span className="text-[#3e4491] text-sm font-medium flex items-center justify-center gap-1">
                        + Add Step
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="order-1 md:order-2 flex flex-col gap-6 text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#292a73]/10 border border-[#3e4491]/25 text-[#292a73] text-xs font-bold">
                <Workflow className="h-3.5 w-3.5 text-[#3a9efd]" />
                <span>No-Code Automation</span>
              </div>
              <h2 className="text-4xl text-[#1a1b4b] font-bold tracking-tight">
                Process automated.
              </h2>
              <p className="text-lg text-[#3e4491] leading-relaxed">
                Turn routine tasks into automated workflows without writing a single line of code. Keep your team moving forward with structured lists, approval flows, and native integrations.
              </p>
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="text-[#3a9efd] h-5 w-5 shrink-0" />
                  <span className="text-[#1a1b4b] font-medium">No-code Visual Builder</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="text-[#3a9efd] h-5 w-5 shrink-0" />
                  <span className="text-[#1a1b4b] font-medium">Dynamic Lists</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="text-[#3a9efd] h-5 w-5 shrink-0" />
                  <span className="text-[#1a1b4b] font-medium">Custom API Triggers</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          FINAL CTA
          ============================================================ */}
      <section className="py-32 px-6 text-center bg-gradient-to-br from-[#1a1b4b] via-[#292a73] to-[#3e4491] text-white relative overflow-hidden">
        <div className="max-w-4xl mx-auto relative z-10 space-y-6">
          <h2 className="text-5xl md:text-6xl font-black tracking-tight drop-shadow-sm">
            See all that you can accomplish in SahajConnect.
          </h2>
          <p className="text-xl max-w-2xl mx-auto text-slate-200 font-medium">
            Join thousands of forward-thinking teams building the future of work.
          </p>
          <div className="pt-4">
            <Link
              to="/register"
              className="bg-[#f7a400] text-[#1a1b4b] px-10 py-4 rounded-full font-bold text-lg shadow-xl hover:bg-white hover:text-[#292a73] transition-all transform hover:-translate-y-1 inline-flex items-center gap-2"
            >
              <span>Get Started Today for Free</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Shared Footer */}
      <Footer />

    </div>
  );
};
